/**
 * Pure shaping for Reports > Check-ins > Competitors. The RPC
 * (`get_competitor_gate_report`, migration 20261013100000) returns one entry
 * per Bali day with 24 hourly buckets for both gyms; everything here is
 * averaging and picking, kept out of the component so it can be tested.
 */
import { getBaliDateKey } from '@/lib';

export interface CompetitorGateHour {
  hour: number;
  /** Gate check-ins in the hour, from counter differences. null = not sampled. */
  reload_checkins: number | null;
  reload_avg_occupancy: number | null;
  reload_max_occupancy: number | null;
  hok_checkins: number;
}

export interface CompetitorGateDay {
  date: string;
  /** Sampled from opening to close (>= 56 of 64 samples, incl. the 22:00 one). */
  complete: boolean;
  samples: number;
  reload_checkins: number | null;
  hok_checkins: number;
  visitors_first: number | null;
  visitors_last: number | null;
  members_last: number | null;
  hours: CompetitorGateHour[];
}

export interface CompetitorGateLatest {
  polled_at: string;
  occupancy: number | null;
  checkins_today: number | null;
  checkouts_today: number | null;
  members: number | null;
  visitors: number | null;
  prev7d_occupancy: number | null;
}

export interface CompetitorGateReport {
  latest: CompetitorGateLatest | null;
  first_polled_at: string | null;
  days: CompetitorGateDay[];
}

/** Three missed 15-minute ticks: older than this, the sampler has stalled. */
export const LIVE_READING_MAX_AGE_MS = 45 * 60 * 1000;

/**
 * Whether the latest reading may be shown as "now" / "today": taken on the
 * current Bali day and recently enough. The RPC returns the newest reading
 * whatever its age, so a stopped sampler would otherwise pass yesterday's
 * occupancy off as live.
 */
export function isLiveReading(latest: CompetitorGateLatest | null, todayKey: string, now: Date = new Date()): boolean {
  if (!latest) return false;
  const polled = new Date(latest.polled_at);
  if (Number.isNaN(polled.getTime())) return false;
  return getBaliDateKey(polled) === todayKey && now.getTime() - polled.getTime() <= LIVE_READING_MAX_AGE_MS;
}

/** Reload opens 06:00, HOK 07:00; both close 22:00. Buckets 06:00-21:00. */
export const COMPETITOR_CHART_HOURS = Array.from({ length: 16 }, (_, i) => i + 6);

export interface CompetitorHourPoint {
  hour: number;
  label: string;
  hok: number | null;
  reload: number | null;
  reloadInside: number | null;
}

const round1 = (value: number) => Math.round(value * 10) / 10;

function mean(values: Array<number | null | undefined>): number | null {
  const present = values.filter((v): v is number => typeof v === 'number' && Number.isFinite(v));
  if (present.length === 0) return null;
  return round1(present.reduce((sum, v) => sum + v, 0) / present.length);
}

export function hourLabel(hour: number): string {
  return `${String(hour).padStart(2, '0')}:00`;
}

/**
 * Average per hour over the given days. Both gyms are averaged over the SAME
 * days, so pass only complete days when comparing; a single day gives that
 * day's own numbers.
 */
export function hourlyComparison(days: CompetitorGateDay[]): CompetitorHourPoint[] {
  return COMPETITOR_CHART_HOURS.map((hour) => {
    const buckets = days.map((d) => d.hours.find((h) => h.hour === hour));
    return {
      hour,
      label: hourLabel(hour),
      hok: days.length ? mean(buckets.map((b) => b?.hok_checkins ?? 0)) : null,
      reload: mean(buckets.map((b) => b?.reload_checkins)),
      reloadInside: mean(buckets.map((b) => b?.reload_avg_occupancy)),
    };
  });
}

export function completeDays(report: CompetitorGateReport): CompetitorGateDay[] {
  return report.days.filter((d) => d.complete);
}

/** Days the sampler saw at all, newest first (for the day picker and table). */
export function sampledDays(report: CompetitorGateReport): CompetitorGateDay[] {
  return report.days.filter((d) => d.samples > 0 || d.reload_checkins !== null).slice().reverse();
}

export interface PeakHour {
  hour: number;
  value: number;
}

export function peakHour(points: CompetitorHourPoint[], key: 'hok' | 'reload'): PeakHour | null {
  let best: PeakHour | null = null;
  for (const p of points) {
    const v = p[key];
    if (v !== null && (best === null || v > best.value)) best = { hour: p.hour, value: v };
  }
  return best;
}

/** Share of the day's check-ins that arrive before 12:00, as a whole percent. */
export function morningShare(points: CompetitorHourPoint[], key: 'hok' | 'reload'): number | null {
  const total = points.reduce((s, p) => s + (p[key] ?? 0), 0);
  if (total <= 0) return null;
  const morning = points.filter((p) => p.hour < 12).reduce((s, p) => s + (p[key] ?? 0), 0);
  return Math.round((100 * morning) / total);
}
